import React, { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Window, WindowHeader, WindowContent, Button, Panel } from 'react95';
import original from 'react95/dist/themes/original';
import NavigatorPanel from './NavigatorPanel';

/**
 * Demo component showcasing NavigatorPanel functionality
 * Demonstrates all the features implemented in task 6:
 * - Organized sections (Debt, Debtor, Dialer, Queue)
 * - Navigation menu with icons and functionality
 * - State-based enabling/disabling of menu items
 * - Integration with main application navigation
 */
const NavigatorPanelDemo = () => {
  const [currentClient, setCurrentClient] = useState(null);
  const [navigationState, setNavigationState] = useState({
    activeSection: 'debt',
    expandedSections: ['debt', 'debtor'],
    availableFunctions: [],
    disabledFunctions: []
  });
  const [navigationLog, setNavigationLog] = useState([]);

  // Sample clients for demonstration
  const sampleClients = [
    { id: '1', clientName: 'John Smith', debtCode: 'DC001' },
    { id: '2', clientName: 'Jane Doe', debtCode: 'DC002' },
    { id: '3', clientName: 'Bob Johnson', debtCode: 'DC003' }
  ];

  // Handle navigation events from the panel
  const handleNavigation = (navigationData) => {
    const { function: functionName, section, client } = navigationData;
    
    // Log the navigation event
    const logEntry = {
      timestamp: new Date().toLocaleTimeString(),
      function: functionName,
      section: section,
      client: client?.clientName || 'None'
    };
    
    setNavigationLog(prev => [logEntry, ...prev.slice(0, 9)]); // Keep last 10 entries
    
    // Update active section
    setNavigationState(prev => ({
      ...prev,
      activeSection: section
    }));
  };

  // Select a client
  const selectClient = (client) => {
    setCurrentClient(client);
    setNavigationState(prev => ({
      ...prev,
      availableFunctions: [], // Enable all functions when client is selected
      disabledFunctions: []
    }));
  };

  // Clear client selection
  const clearClient = () => {
    setCurrentClient(null);
    setNavigationState(prev => ({
      ...prev,
      availableFunctions: ['client-search', 'new-client', 'reports', 'settings'],
      disabledFunctions: ['client-details', 'payment-arrangement', 'transaction-history']
    }));
  };

  // Toggle specific functions
  const toggleFunction = (functionName) => {
    setNavigationState(prev => {
      const isDisabled = prev.disabledFunctions.includes(functionName);
      return {
        ...prev,
        disabledFunctions: isDisabled
          ? prev.disabledFunctions.filter(f => f !== functionName)
          : [...prev.disabledFunctions, functionName]
      };
    });
  };

  return (
    <ThemeProvider theme={original}>
      <div style={{ 
        padding: '20px', 
        backgroundColor: '#c0c0c0', 
        minHeight: '100vh',
        display: 'flex',
        gap: '20px'
      }}>
        
        {/* Navigator Panel */}
        <NavigatorPanel
          currentClient={currentClient}
          onNavigate={handleNavigation}
          navigationState={navigationState}
        />

        {/* Demo Controls and Information */}
        <div style={{ flex: 1 }}>
          
          {/* Client Selection */}
          <Window style={{ marginBottom: '20px' }}>
            <WindowHeader>Client Selection</WindowHeader>
            <WindowContent style={{ padding: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Select a client to enable client-specific functions:</strong>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                {sampleClients.map(client => (
                  <Button
                    key={client.id}
                    onClick={() => selectClient(client)}
                    style={{ 
                      backgroundColor: currentClient?.id === client.id ? '#0066cc' : undefined,
                      color: currentClient?.id === client.id ? 'white' : undefined
                    }}
                  >
                    {client.clientName}
                  </Button>
                ))}
                <Button onClick={clearClient}>Clear Selection</Button>
              </div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                Current Client: {currentClient ? `${currentClient.clientName} (${currentClient.debtCode})` : 'None'}
              </div>
            </WindowContent>
          </Window>

          {/* Function Controls */}
          <Window style={{ marginBottom: '20px' }}>
            <WindowHeader>Function Controls</WindowHeader>
            <WindowContent style={{ padding: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Toggle function availability:</strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
                {['debt-summary', 'payment-history', 'client-details', 'make-call', 'work-queue', 'reports'].map(func => (
                  <Button
                    key={func}
                    onClick={() => toggleFunction(func)}
                    size="sm"
                    style={{
                      backgroundColor: navigationState.disabledFunctions.includes(func) ? '#ff6666' : '#66ff66',
                      fontSize: '10px'
                    }}
                  >
                    {func.replace('-', ' ')}
                  </Button>
                ))}
              </div>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                Green = Enabled, Red = Disabled
              </div>
            </WindowContent>
          </Window>

          {/* Navigation State */}
          <Window style={{ marginBottom: '20px' }}>
            <WindowHeader>Navigation State</WindowHeader>
            <WindowContent style={{ padding: '10px' }}>
              <div style={{ fontSize: '11px' }}>
                <div><strong>Active Section:</strong> {navigationState.activeSection}</div>
                <div><strong>Expanded Sections:</strong> {navigationState.expandedSections.join(', ')}</div>
                <div><strong>Disabled Functions:</strong> {navigationState.disabledFunctions.length > 0 ? navigationState.disabledFunctions.join(', ') : 'None'}</div>
              </div>
            </WindowContent>
          </Window>

          {/* Navigation Log */}
          <Window>
            <WindowHeader>Navigation Log</WindowHeader>
            <WindowContent style={{ padding: '10px' }}>
              <div style={{ fontSize: '11px', maxHeight: '200px', overflow: 'auto' }}>
                {navigationLog.length === 0 ? (
                  <div style={{ color: '#666' }}>Click navigation items to see activity...</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0' }}>
                        <th style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'left' }}>Time</th>
                        <th style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'left' }}>Function</th>
                        <th style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'left' }}>Section</th>
                        <th style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'left' }}>Client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {navigationLog.map((entry, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #ccc', padding: '4px' }}>{entry.timestamp}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px' }}>{entry.function}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px' }}>{entry.section}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px' }}>{entry.client}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </WindowContent>
          </Window>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default NavigatorPanelDemo;